import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {AlphaLabel} from '@github-ui/lifecycle-labels/alpha'
import {TabNav} from '@primer/react/deprecated'
import {clsx} from 'clsx'

import styles from './ViewSwitch.module.css'

export type MarkdownViewMode = 'preview' | 'edit' | 'hybrid'
export type MarkdownDefaultViewMode = 'edit' | 'hybrid'

type ViewSwitchProps = {
  selectedView: MarkdownViewMode
  onViewSelect?: (view: MarkdownViewMode) => void
  disabled?: boolean
  /** Called when the preview should be loaded. */
  onLoadPreview?: () => void
}

// no point in memoizing this component because onLoadPreview depends on value, so it would still re-render on every change
export const ViewSwitch = ({
  selectedView,
  onViewSelect: externalOnViewSelect,
  onLoadPreview,
  disabled,
}: ViewSwitchProps) => {
  // don't get disabled from context - the switch is not disabled when the editor is disabled
  const hybridEnabled = Boolean(isFeatureEnabled('markdown_experience_hybrid_editor'))

  const onViewSelect = (mode: MarkdownViewMode) => {
    sendEvent(`markdown_editor.select_view`, {
      mode,
      ['from_mode']: selectedView,
      ['trigger']: 'view_switcher',
      ['did_change']: selectedView !== mode,
      ['feature_flag_markdown_experience_hybrid_editor']: hybridEnabled,
    })
    externalOnViewSelect?.(mode)
  }

  return (
    <div className={styles.viewSwitch}>
      <TabNav aria-label="View mode">
        <TabNav.Link
          as="button"
          selected={selectedView === 'edit'}
          disabled={disabled}
          className={clsx(styles.tabNavLink, selectedView === 'edit' && styles.active)}
          onClick={() => onViewSelect?.('edit')}
        >
          Write
        </TabNav.Link>
        <TabNav.Link
          as="button"
          selected={selectedView === 'preview'}
          disabled={disabled}
          className={clsx(styles.tabNavLink, selectedView === 'preview' && styles.active)}
          onClick={() => onViewSelect?.('preview')}
          onMouseOver={() => onLoadPreview?.()}
          onFocus={() => onLoadPreview?.()}
        >
          Preview
        </TabNav.Link>
        {hybridEnabled && (
          <TabNav.Link
            as="button"
            selected={selectedView === 'hybrid'}
            disabled={disabled}
            className={clsx(styles.tabNavLink, selectedView === 'hybrid' && styles.active)}
            onClick={() => onViewSelect?.('hybrid')}
          >
            Hybrid
            <AlphaLabel className={styles.lifecycleLabel} />
          </TabNav.Link>
        )}
      </TabNav>
    </div>
  )
}

try{ ViewSwitch.displayName ||= 'ViewSwitch' } catch {}